import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Card,
  Typography,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { FINAL_SUBSCRIPTION_PRICE, ORIGINAL_SUBSCRIPTION_PRICE } from '../common/AppConstants';
import RazorpayPaymentButton from './RazorpayPaymentButton';

// Enhanced features supporting both boolean and text values
const features = [
  {
    name: 'TimeTables',
    type: 'text',
    free: '3/year',
    premium: 'Unlimited',
  },
  {
    name: 'Substitutes',
    type: 'text',
    free: '3/month',
    premium: 'Unlimited',
  },
  {
    name: 'Email',
    type: 'boolean',
    free: true,
    premium: true,
  },
  {
    name: 'Whatsapp',
    type: 'boolean',
    free: false,
    premium: true,
  },
  {
    name: 'Bulk upload',
    type: 'boolean',
    free: true,
    premium: true,
  },
  {
    name: 'Excel export',
    type: 'boolean',
    free: true,
    premium: true,
  },
];

/**
 * Component to render feature value based on type
 * Supports both boolean (tick/cross) and text values
 */
const FeatureValue = ({ value, type, isHighlighted = false }) => {
  if (type === 'boolean') {
    return value ? (
      <CheckCircleIcon
        sx={{
          color: isHighlighted ? 'success.main' : 'success.light',
          fontSize: 22
        }}
      />
    ) : (
      <CancelIcon sx={{ color: 'grey.400', fontSize: 22 }} />
    );
  }
 
  // Text type - handle various text values
  return (
    <Typography
      variant="body2"
      fontWeight={isHighlighted ? 600 : 500}
      color={isHighlighted ? 'success.dark' : 'text.primary'}
      sx={{
        textAlign: 'center',
        lineHeight: 1.4,
        fontSize: '0.875rem',
      }}
    >
      {value}
    </Typography>
  );
};

export default function UpgradeModal({ open, handleClose }) {
  const razorpayButtonRef = useRef();
  const loading = false;

  const handleUpgradeClick = () => {
    razorpayButtonRef.current?.click();
  };

  const monthlyPriceExcludingGST = (FINAL_SUBSCRIPTION_PRICE / 1.18 / 12).toFixed(2);
  const discountPercent = Math.round(
    ((ORIGINAL_SUBSCRIPTION_PRICE - FINAL_SUBSCRIPTION_PRICE) / ORIGINAL_SUBSCRIPTION_PRICE) * 100
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
        }}
      >
        {/* Left side: Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h3">
            Upgrade to Premium
          </Typography>
        </Box>

        {/* Right side: Close button */}
        <IconButton
          edge="end"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
     
      <DialogContent sx={{ py: 4, px: { xs: 2, sm: 4 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 4 }}
          alignItems="stretch"
        >
          {/* Column 1: Feature Labels (Desktop Only) */}
          <Box
            sx={{
              flex: '0 0 200px',
              display: { xs: 'none', md: 'block' },
            }}
          >
            {/* Spacer for card header */}
            <Box sx={{ height: 160, mb: 2 }} />
           
            {features.map((feature, idx) => (
              <Box
                key={idx}
                sx={{
                  py: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 56,
                  borderBottom: idx < features.length - 1 ? '1px solid' : 'none',
                  borderColor: 'grey.200',
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="text.primary"
                >
                  {feature.name}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Column 2: Free Trial Plan */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card
              variant="outlined"
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: 'grey.300',
                borderRadius: 3,
                boxShadow: 1,
                textAlign: 'center',
                height: '100%',
                opacity: 0.75,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
            >
              {/* Card Header */}
              <Chip
                label="Current Plan"
                size="small"
                sx={{
                  mb: 1,
                  bgcolor: 'grey.200',
                  fontWeight: 600
                }}
              />
              <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                Free Trial
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Limited access
              </Typography>

              {/* Price */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  ₹0
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ py: 0.5 }}
                >
                  <Typography
                      component="body2"
                      sx={{ textDecoration: 'line-through', mr: 1}}
                    ></Typography>
                    <Typography
                    component="body2"
                    sx={{
                      // backgroundColor: 'success.light',
                      color: 'success.dark',
                      borderRadius: 1,
                      fontWeight: 'bold'
                    }}
                  ></Typography>
                </Typography>
              </Box>

              {/* Feature Values */}
              <Box sx={{ mt: 2 }}>
                {features.map((feature, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: { xs: 'flex', md: 'block' },
                      alignItems: 'center',
                      justifyContent: { xs: 'space-between', md: 'center' },
                      py: 2.5,
                      minHeight: 56,
                      borderBottom: idx < features.length - 1 ? '1px solid' : 'none',
                      borderColor: 'grey.200',
                    }}
                  >
                    {/* Mobile: Show feature name */}
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mr: 2,
                        flex: 1,
                        textAlign: 'left'
                      }}
                    >
                      {feature.name}
                    </Typography>
                   
                    {/* Feature value (tick/cross or text) */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      minWidth: { xs: 80, md: 'auto' }
                    }}>
                      <FeatureValue
                        value={feature.free}
                        type={feature.type}
                        isHighlighted={false}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          </Box>

          {/* Column 3: Premium Plan (Highlighted) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
                boxShadow: 4,
                textAlign: 'center',
                height: '100%',
                position: 'relative',
                cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 8,
                },
                // Recommended badge
                // '&::before': {
                //   content: '"Recommended"',
                //   position: 'absolute',
                //   top: -12,
                //   left: 16,
                //   backgroundColor: 'success.main',
                //   color: 'white',
                //   fontSize: '0.75rem',
                //   fontWeight: 'bold',
                //   px: 2,
                //   py: 0.5,
                //   borderRadius: '12px',
                //   boxShadow: '2px 2px 8px rgba(0,0,0,0.15)',
                //   zIndex: 1,
                // },
              }}
              onClick={handleUpgradeClick}
            >
              {/* Card Header */}
              <Chip
                label="Best Value"
                size="small"
                color="success"
                sx={{
                  mb: 1,
                  fontWeight: 600
                }}
              />
              <Typography variant="h5" fontWeight="bold" color="success.dark" gutterBottom>
                Premium
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Full feature access
              </Typography>
              
              {/* Price */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="success.main"
                >
                  ₹{FINAL_SUBSCRIPTION_PRICE}/year
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ py: 0.5 }}
                >
                  <Typography
                    component="body2"
                    sx={{ textDecoration: 'line-through', mr: 1}}
                  >
                    ₹{ORIGINAL_SUBSCRIPTION_PRICE}
                  </Typography>
                  <Typography
                    component="body2"
                    sx={{
                      // backgroundColor: 'success.light',
                      color: 'success.dark',
                      borderRadius: 1,
                      fontWeight: 'bold'
                    }}
                  >
                    {discountPercent}% off
                  </Typography>
                </Typography>
              </Box>
              

              {/* Feature Values */}
              <Box sx={{ mt: 2 }}>
                {features.map((feature, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: { xs: 'flex', md: 'block' },
                      alignItems: 'center',
                      justifyContent: { xs: 'space-between', md: 'center' },
                      py: 2.5,
                      minHeight: 56,
                      borderBottom: idx < features.length - 1 ? '1px solid' : 'none',
                      borderColor: 'success.100',
                    }}
                  >
                    {/* Mobile: Show feature name */}
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mr: 2,
                        flex: 1,
                        textAlign: 'left'
                      }}
                    >
                      {feature.name}
                    </Typography>
                   
                    {/* Feature value (tick/cross or text) */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      minWidth: { xs: 80, md: 'auto' }
                    }}>
                      <FeatureValue
                        value={feature.premium}
                        type={feature.type}
                        isHighlighted={true}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              {loading && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ mt: 2, display: 'block' }}
                >
                  Processing payment...
                </Typography>
              )}
            </Card>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            px: 4,
            padding: '8px 24px',
            fontSize: '16px',
          }}
        >
          Cancel
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <RazorpayPaymentButton
            button_text="Upgrade Now"
            ref={razorpayButtonRef}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
